import { registerPlugin } from '@capacitor/core';

/**
 * Entrega de ficheros al dispositivo dentro de la app Android.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 *
 * En el navegador, descargar es pinchar un `<a download>` con una URL `blob:`.
 * Dentro del APK eso NO HACE NADA: el WebView de Android no interpreta las URL
 * `blob:`, así que la petición de descarga no llega a formarse y el
 * `DownloadListener` —el punto donde una app nativa atiende las descargas de su
 * WebView— no se dispara nunca. Ni lo cablea Capacitor ni lo cableábamos
 * nosotros. Es una limitación conocida y sin arreglo oficial
 * (ionic-team/capacitor#5478, abierta desde marzo de 2022).
 *
 * ── Por qué la escritura va a TROZOS ───────────────────────────────────────
 *
 * Porque el puente tiene un techo, y pasarlo no da un error: da un CUELGUE.
 *
 * `postToNative` de Capacitor hace `androidBridge.postMessage(JSON.stringify(…))`
 * dentro de un `try/catch` que **solo hace `console.error`**. Si el mensaje es
 * demasiado grande, Java lanza, Capacitor se lo traga, y la promesa del plugin
 * **no se resuelve ni se rechaza jamás**. Medido en tablet con un ZIP de 211
 * fotos: la pantalla se quedó con el indicador girando indefinidamente.
 *
 * Por eso el contenido no viaja de una vez: `beginWrite` abre, `writeChunk`
 * escribe trozos que caben de sobra, y `endWrite` cierra.
 *
 * ── Por qué un plugin propio y no `@capacitor/filesystem` ──────────────────
 *
 * Porque el objetivo es que el fichero acabe en **Descargas**, donde el usuario
 * lo busca. `@capacitor/filesystem` no puede: `Directory.ExternalStorage` es
 * solo Android ≤ 9 y `Directory.Documents` en Android 11+ queda confinado a los
 * ficheros de la propia app —invisible para el usuario—. Llegar a Descargas
 * exige `MediaStore`, o sea código nativo.
 *
 * Es el cuarto plugin propio del proyecto, junto a `roisBackgroundLocation`,
 * `roisAppUpdate` y `roisCredentials`. Sin dependencias de terceros.
 *
 * ── Fuera de la app nativa ────────────────────────────────────────────────
 *
 * No se usa: `saveBlob` ni llega a llamarlo (ver `utils/saveBlob.ts`).
 */

export interface BeginWriteOptions {
  /** Nombre CON extensión; Android deriva de él la entrada en Descargas. */
  filename: string;
  /** MIME real del contenido. Decide qué apps ofrece Android al abrirlo. */
  mimeType: string;
}

export interface WriteHandle {
  /** Identifica la escritura en curso en las llamadas siguientes. */
  token: string;
}

export interface WriteChunkOptions {
  token: string;
  /** Trozo del contenido en base64, SIN el prefijo `data:...;base64,`. */
  base64: string;
}

export interface SavedFile {
  /** `content://` en Android 10+; `file://` en Android 7-9. */
  uri: string;
}

export interface OpenFileOptions {
  uri: string;
  mimeType: string;
}

export interface RoisFileSaverPlugin {
  /**
   * Abre el fichero destino y devuelve el identificador para escribirlo.
   *
   * En Android 10+ la entrada nace OCULTA (`IS_PENDING`), así que nadie ve un
   * fichero a medio escribir. Rechaza con `FILE_SAVE_FAILED`.
   */
  beginWrite(options: BeginWriteOptions): Promise<WriteHandle>;

  /**
   * Escribe un trozo.
   *
   * Si falla, el lado nativo DESCARTA la escritura entera: media descarga es
   * peor que ninguna, porque parece completa.
   */
  writeChunk(options: WriteChunkOptions): Promise<void>;

  /** Cierra el fichero, lo hace visible y devuelve su URI. */
  endWrite(options: WriteHandle): Promise<SavedFile>;

  /** Cancela una escritura sin dejar rastro. Nunca rechaza. */
  abortWrite(options: WriteHandle): Promise<void>;

  /**
   * Abre un fichero ya guardado con la app que corresponda.
   *
   * Va SEPARADO del guardado a propósito: guardar tiene que funcionar aunque en
   * la tablet no haya ninguna app capaz de abrir un PDF (caso real en una
   * tablet pelada). En ese supuesto el fichero YA está en Descargas, que es lo
   * que de verdad importa, y este método rechaza con `FILE_OPEN_NO_APP` sin
   * arrastrar consigo el guardado.
   */
  open(options: OpenFileOptions): Promise<void>;
}

export const RoisFileSaver = registerPlugin<RoisFileSaverPlugin>('RoisFileSaver');

export default RoisFileSaver;
